/**
 * Vera Ideas — React Query hooks. Query keys all start with 'ideas' so the
 * module can be invalidated wholesale; the vote mutation patches caches
 * optimistically so the thumbs-up feels instant.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  addComment,
  addVote,
  convertToTicket,
  createIdea,
  createPrompt,
  deleteComment,
  deleteIdea,
  deletePrompt,
  fetchAdminPrompts,
  fetchAdminSummary,
  fetchIdea,
  fetchIdeas,
  fetchMe,
  fetchNotifications,
  fetchPrompts,
  fetchSimilar,
  fetchTags,
  markNotificationsRead,
  mergeIdea,
  removeVote,
  setIdeaStatus,
  updateIdea,
  updatePrompt,
  type IdeaDetailResult,
  type IdeaPatch,
  type IdeasPage,
  type ListIdeasParams,
  type NewIdea,
  type PromptPatch,
  type StatusChange,
} from './ideas';

const LIST_KEY = ['ideas', 'list'] as const;
const detailKey = (id: string) => ['ideas', 'detail', id] as const;

export function useIdeasMe() {
  return useQuery({ queryKey: ['ideas', 'me'], queryFn: fetchMe, staleTime: 5 * 60 * 1000 });
}

export function useIdeaTags() {
  return useQuery({ queryKey: ['ideas', 'tags'], queryFn: fetchTags, staleTime: 60 * 60 * 1000 });
}

export function useIdeasList(params: Omit<ListIdeasParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: [...LIST_KEY, params],
    queryFn: ({ pageParam }) => fetchIdeas({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });
}

/** Ideas shipped recently, for the "Recently shipped 🎉" strip. */
export function useRecentlyShipped() {
  return useQuery({
    queryKey: ['ideas', 'recently-shipped'],
    queryFn: () => fetchIdeas({ sort: 'new', statuses: ['shipped'], pageSize: 10 }),
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      const cutoff = Date.now() - 60 * 24 * 60 * 60 * 1000;
      return data.ideas.filter(
        (i) => i.shipped_at && new Date(i.shipped_at).getTime() >= cutoff
      );
    },
  });
}

export function useIdea(id: string | undefined) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => fetchIdea(id!),
    enabled: !!id,
  });
}

export function useSimilarIdeas(title: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ideas', 'similar', title],
    queryFn: () => fetchSimilar(title),
    enabled: enabled && title.trim().length >= 3,
    staleTime: 30 * 1000,
  });
}

function useIdeasInvalidation() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: ['ideas', 'recently-shipped'] });
    if (id) void queryClient.invalidateQueries({ queryKey: detailKey(id) });
  };
}

export function useCreateIdea() {
  const invalidate = useIdeasInvalidation();
  return useMutation({ mutationFn: (input: NewIdea) => createIdea(input), onSuccess: () => invalidate() });
}

export function useUpdateIdea() {
  const invalidate = useIdeasInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: IdeaPatch }) => updateIdea(id, patch),
    onSuccess: (_d, { id }) => invalidate(id),
  });
}

export function useDeleteIdea() {
  const invalidate = useIdeasInvalidation();
  return useMutation({ mutationFn: (id: string) => deleteIdea(id), onSuccess: () => invalidate() });
}

/**
 * Optimistic thumbs-up toggle: every list page and the detail cache flip
 * immediately; a failed request rolls back via invalidation.
 */
export function useToggleVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hasVoted }: { id: string; hasVoted: boolean }) =>
      hasVoted ? removeVote(id) : addVote(id),
    onMutate: async ({ id, hasVoted }) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const delta = hasVoted ? -1 : 1;
      queryClient.setQueriesData<InfiniteData<IdeasPage>>({ queryKey: LIST_KEY }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((p) => ({
                ...p,
                ideas: p.ideas.map((i) =>
                  i.id === id
                    ? { ...i, vote_count: Math.max(0, i.vote_count + delta), viewer_has_voted: !hasVoted }
                    : i
                ),
              })),
            }
          : data
      );
      queryClient.setQueryData<IdeaDetailResult>(detailKey(id), (data) =>
        data && 'idea' in data
          ? {
              idea: {
                ...data.idea,
                vote_count: Math.max(0, data.idea.vote_count + delta),
                viewer_has_voted: !hasVoted,
              },
            }
          : data
      );
    },
    onError: (_err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: detailKey(id) });
    },
  });
}

export function useAddComment() {
  const invalidate = useIdeasInvalidation();
  return useMutation({
    mutationFn: ({ ideaId, body }: { ideaId: string; body: string }) => addComment(ideaId, body),
    onSuccess: (_d, { ideaId }) => invalidate(ideaId),
  });
}

export function useDeleteComment() {
  const invalidate = useIdeasInvalidation();
  return useMutation({
    mutationFn: ({ commentId }: { commentId: string; ideaId: string }) => deleteComment(commentId),
    onSuccess: (_d, { ideaId }) => invalidate(ideaId),
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['ideas', 'notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 60 * 1000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['ideas', 'notifications'] }),
  });
}

export function useSetIdeaStatus() {
  const invalidate = useIdeasInvalidation();
  return useMutation({
    mutationFn: ({ id, change }: { id: string; change: StatusChange }) => setIdeaStatus(id, change),
    onSuccess: (_d, { id }) => invalidate(id),
  });
}

export function useMergeIdea() {
  const invalidate = useIdeasInvalidation();
  return useMutation({
    mutationFn: ({ id, intoIdeaId }: { id: string; intoIdeaId: string }) => mergeIdea(id, intoIdeaId),
    onSuccess: (result, { id }) => {
      invalidate(id);
      invalidate(result.intoIdeaId);
    },
  });
}

export function useConvertToTicket() {
  return useMutation({ mutationFn: (id: string) => convertToTicket(id) });
}

// ---------------------------------------------------------------------------
// Rotating prompts + admin summary
// ---------------------------------------------------------------------------

const PROMPTS_KEY = ['ideas', 'prompts'] as const;
const ADMIN_PROMPTS_KEY = ['ideas', 'admin-prompts'] as const;

export function usePrompts() {
  return useQuery({ queryKey: PROMPTS_KEY, queryFn: fetchPrompts, staleTime: 5 * 60 * 1000 });
}

export function useAdminPrompts(enabled: boolean) {
  return useQuery({ queryKey: ADMIN_PROMPTS_KEY, queryFn: fetchAdminPrompts, enabled });
}

function usePromptsInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PROMPTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_PROMPTS_KEY });
  };
}

export function useCreatePrompt() {
  const invalidate = usePromptsInvalidation();
  return useMutation({ mutationFn: (text: string) => createPrompt(text), onSuccess: invalidate });
}

export function useUpdatePrompt() {
  const invalidate = usePromptsInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: PromptPatch }) => updatePrompt(id, patch),
    onSuccess: invalidate,
  });
}

export function useDeletePrompt() {
  const invalidate = usePromptsInvalidation();
  return useMutation({ mutationFn: (id: number) => deletePrompt(id), onSuccess: invalidate });
}

export function useAdminSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['ideas', 'admin-summary'],
    queryFn: fetchAdminSummary,
    enabled,
    staleTime: 60 * 1000,
  });
}
